<script setup lang="ts">
import { useRoute } from "vue-router";
import { usePlaceMenuStore } from "@/modules/place-module";
import { computed, onBeforeMount, onMounted, ref } from "vue";
import { UiChip } from "@packages/component-library";
import PlaceProductCard from "./PlaceProductCard.vue";
import PlaceProductCardSkeleton from "./PlaceProductCardSkeleton.vue";
import AppPagination from "@/components/AppPagination.vue";
import PlaceAuthBanner from "@/modules/place-module/components/PlaceMenu/PlaceAuthBanner.vue";
import { useUserStore } from "@/modules/user-module";

const route = useRoute();
const userStore = useUserStore();
const user = computed(() => userStore.user);
const placeMenuStore = usePlaceMenuStore();
const menuLoadingStatus = computed(() => placeMenuStore.menuLoadingStatus);
const categories = computed(() => placeMenuStore.categories);
const selectedCategory = computed(() => placeMenuStore.selectedCategory);
const products = computed(() => placeMenuStore.products);
const pagination = computed(() => placeMenuStore.pagination);
const pageLoading = ref(false);
const page = ref(1);

const selectCategory = (category: number) => {
  page.value = 1;
  placeMenuStore.selectMenuCategory(category);
  loadPlaceProducts();
};

const loadNextPage = () => {
  page.value++;
  loadPlaceProducts();
};

const loadPlaceProducts = () => {
  pageLoading.value = true;
  placeMenuStore
    .loadPlaceProducts(
      route.query.place,
      selectedCategory.value || 0,
      page.value
    )
    .finally(() => {
      pageLoading.value = false;
    });
};

onBeforeMount(() => {
  loadPlaceProducts();
});

onMounted(() => {
  document
    .getElementById("place-menu")
    .scrollIntoView({ block: "start", behavior: "smooth" });
});
</script>

<template>
  <PlaceAuthBanner v-if="!user" class="menu-banner" />

  <section id="place-menu" class="place-menu">
    <div v-if="categories?.length" class="place-menu__categories">
      <UiChip
        v-for="(category, index) in categories"
        :key="index"
        :label="category.title"
        size="m"
        :active="category.id === selectedCategory"
        @click="selectCategory(category.id)"
      />
    </div>

    <div class="place-menu__products">
      <template v-if="menuLoadingStatus === 'loaded'">
        <PlaceProductCard
          v-for="(product, index) in products"
          :key="index"
          :product="product"
        />
      </template>

      <template v-else>
        <PlaceProductCardSkeleton v-for="item in 4" :key="item" />
      </template>
    </div>

    <AppPagination
      v-if="
        pagination &&
        pagination.has_more_pages &&
        menuLoadingStatus === 'loaded'
      "
      :page-loading="pageLoading"
      @nextPage="loadNextPage"
    />
  </section>
</template>

<style scoped lang="scss">
.place-menu {
  padding: calc($base-space * 6) calc($base-space * 8);

  @media (max-width: 1023px) {
    padding: calc($base-space * 6);
  }

  @media (max-width: 767px) {
    padding: calc($base-space * 5) calc($base-space * 4);
  }

  &__categories {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: calc($base-space * 2);
    overflow: auto;
    @include hideScroll();
    margin: 0 calc($base-space * -8) 0 0;
    padding: 0 calc($base-space * 8) 0 0;

    @media (max-width: 1023px) {
      margin: 0 calc($base-space * -6) 0 0;
      padding: 0 calc($base-space * 6) 0 0;
    }

    @media (max-width: 767px) {
      margin: 0 calc($base-space * -4) 0 0;
      padding: 0 calc($base-space * 4) 0 0;
    }

    > .ui-chip {
      flex-shrink: 0;
    }
  }

  &__products {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: calc($base-space * 6) calc($base-space * 4);

    &:not(:first-child) {
      margin: calc($base-space * 6) 0 0;

      @media (max-width: 767px) {
        margin: calc($base-space * 5) 0 0;
      }
    }

    @media (max-width: 1023px) {
      gap: calc($base-space * 6) calc($base-space * 3);
    }

    @media (max-width: 767px) {
      gap: calc($base-space * 5) calc($base-space * 3);
    }
  }

  > .app-pagination {
    margin: calc($base-space * 4) 0 0;
  }
}

.menu-banner {
  border-top: none;
  border-bottom: 1px solid var(--color-semantic-border-normal-soft);
}
</style>
